import { Filter, Layers, Mail, Search, UserPlus } from "lucide-react"

import type { ActionDefinition, ActionDocumentation, ResultSchema } from "@/components/automations/types"

const CATEGORIZE_RESULT_SCHEMA: ResultSchema = {
  category: { type: "string", description: "Selected category" },
  confidence_score: { type: "number", description: "Confidence score (0-100)" },
  reasoning: { type: "string", description: "Reasoning for the selection" },
}

const RESEARCH_RESULT_SCHEMA: ResultSchema = {
  summary: { type: "string", description: "Research step summary" },
}

const DRAFT_EMAIL_RESULT_SCHEMA: ResultSchema = {
  subject: { type: "string", description: "Email subject line" },
  body: { type: "string", description: "Email body content (HTML)" },
}

const CATEGORIZE_DOCUMENTATION: ActionDocumentation = {
  overview:
    "Automatically sorts clients, prospects, or other records into categories you define. Great for segmenting your book or prioritizing outreach.",
  contextProvided: [
    { label: "Profile", description: "Name, type, tier, and tags" },
    { label: "People", description: "Head of household and spouse info" },
    { label: "Custom fields", description: "All custom field values" },
    { label: "Memory bank", description: "Notes and summaries you've saved" },
  ],
  examples: [
    {
      scenario: "Segment by service tier",
      prompt: "A-tier (our most engaged clients), B-tier (regular contact), C-tier (annual check-ins only)",
    },
    {
      scenario: "Classify by service needs",
      prompt: "Retirement Planning, Tax Optimization, Estate Planning, General Wealth Management",
    },
  ],
}

const FILTER_DOCUMENTATION: ActionDocumentation = {
  overview:
    "Decides which records should continue through the automation based on criteria you describe. Records that don't match are skipped.",
  contextProvided: [
    { label: "Profile", description: "Name, type, tier, and tags" },
    { label: "People", description: "Head of household, spouse, and their contact info" },
    { label: "Custom fields", description: "All custom field values" },
    { label: "Memory bank", description: "Notes and summaries you've saved" },
    { label: "Email content", description: "Sender, subject line, and message body for email-triggered automations" },
  ],
  examples: [
    {
      scenario: "By location",
      prompt: "Continue if they live in California, Nevada, or Arizona",
    },
    {
      scenario: "By tier",
      prompt: "Only continue for A-tier and B-tier clients",
    },
    {
      scenario: "Qualify an unknown sender",
      prompt: "Continue if this email is from a customer or a genuine prospect, not a vendor or spam",
    },
  ],
}

const DRAFT_EMAIL_DOCUMENTATION: ActionDocumentation = {
  overview: "Writes a personalized email draft based on your instructions. You can review and edit before sending.",
  contextProvided: [
    { label: "Profile", description: "Name, type, tier, and tags" },
    { label: "People", description: "Head of household, spouse, and their contact info" },
    { label: "Custom fields", description: "All custom field values" },
    { label: "Memory bank", description: "Notes and summaries you've saved" },
  ],
  examples: [
    {
      scenario: "Quarterly check-in",
      prompt:
        "Write a friendly check-in asking about any changes to their financial goals and offering to schedule a review",
    },
    {
      scenario: "Birthday greeting",
      prompt: "Draft a warm birthday message mentioning our appreciation for their trust",
    },
  ],
}

const RESEARCH_DOCUMENTATION: ActionDocumentation = {
  overview:
    "Searches through your emails, meetings, tasks, notes, and even the web to find relevant information and summarize it for you.",
  contextProvided: [
    { label: "Profile", description: "Name, type, tier, and tags" },
    { label: "People", description: "Head of household, spouse, and their contact info" },
  ],
  availableTools: [
    { name: "Emails", description: "Search your email history with this client" },
    { name: "Meetings", description: "Find past meetings and what was discussed" },
    { name: "Tasks", description: "Check tasks and their status" },
    { name: "Notes", description: "Search notes you've saved about them" },
    { name: "Web", description: "Search the internet for public info" },
  ],
  examples: [
    {
      scenario: "Meeting prep",
      prompt: "What have we discussed in the last 90 days? Any concerns or opportunities I should bring up?",
    },
    {
      scenario: "Background check",
      prompt: "What's their professional background? Any news or updates about them?",
    },
  ],
}

const PARSE_CONTACT_DOCUMENTATION: ActionDocumentation = {
  overview:
    "Reads an email or meeting invite and automatically creates a new client, prospect, or contact from the details it finds. For households, also extracts a spouse or partner if one is mentioned and adds them to the same household.",
  contextProvided: [
    { label: "Email content", description: "Subject line and body text" },
    { label: "Meeting details", description: "Title, description, and external attendees" },
  ],
  examples: [
    {
      scenario: "Referral intake",
      prompt: "The referrer's name is usually in the subject line",
    },
    {
      scenario: "Conference follow-up",
      prompt: "Look for job titles in the email signature",
    },
    {
      scenario: "Couple intake form",
      prompt: "If the form has spouse_name and spouse_email fields, extract them as the spouse on the same household",
    },
  ],
}

export const aiActions: ActionDefinition[] = [
  {
    key: "action__ai_categorize",
    name: "Categorize",
    description: "Use AI to categorize data into categories",
    category: "ai",
    nodeType: "action",
    icon: Layers,
    resultSchema: CATEGORIZE_RESULT_SCHEMA,
    documentation: CATEGORIZE_DOCUMENTATION,
    fields: [
      {
        key: "record_type",
        label: "Object to categorize",
        inputType: "select",
        required: true,
        subjectOptions: true,
        options: [
          { value: "household", label: "Household" },
          { value: "contact", label: "Contact" },
          { value: "task", label: "Task" },
          { value: "opportunity", label: "Opportunity" },
          { value: "meeting", label: "Meeting" },
          { value: "person", label: "Person" },
        ],
      },
      {
        key: "categorization_mode",
        label: "Category source",
        inputType: "select",
        required: true,
        options: [
          { value: "custom_categories", label: "Custom categories" },
          { value: "object_field", label: "Object field options" },
        ],
      },
      {
        key: "record_field_name",
        label: "Field to categorize into",
        inputType: "select",
        required: false,
        options: [],
        visibleWhen: { field: "categorization_mode", values: ["object_field"] },
        helperText: "Select a field from the chosen object type",
      },
      {
        key: "categories",
        label: "Categories",
        inputType: "category_builder",
        required: false,
        visibleWhen: { field: "categorization_mode", values: ["custom_categories"] },
        chatValueShape: '[{"id":"<uuid>","name":"...","description":"..."}]',
        helperText: "Add categories with name (required) and description (optional)",
      },
    ],
  },
  {
    key: "action__ai_filter",
    name: "Filter",
    description: "Use AI to filter data based on criteria",
    category: "ai",
    nodeType: "action",
    icon: Filter,
    documentation: FILTER_DOCUMENTATION,
    fields: [
      {
        key: "record_type",
        label: "Record type",
        inputType: "select",
        required: true,
        subjectOptions: true,
        options: [
          { value: "household", label: "Household" },
          { value: "task", label: "Task" },
          { value: "opportunity", label: "Opportunity" },
          { value: "meeting", label: "Meeting" },
          { value: "person", label: "Person" },
          { value: "email_message", label: "Email message" },
        ],
      },
      {
        key: "text_prompt",
        label: "Filter criteria",
        inputType: "textarea",
        required: true,
        helperText: "Describe the criteria for records to continue through the automation.",
        supportsInterpolation: true,
      },
    ],
  },
  {
    key: "action__ai_draft_email",
    name: "Draft email",
    description: "Use AI to draft an email",
    category: "ai",
    nodeType: "action",
    icon: Mail,
    requiresTriggerKeys: ["trigger__household_magic_button", "trigger__meeting_magic_button"],
    resultSchema: DRAFT_EMAIL_RESULT_SCHEMA,
    documentation: DRAFT_EMAIL_DOCUMENTATION,
    fields: [
      {
        key: "record_type",
        label: "Record type",
        inputType: "select",
        required: true,
        subjectOptions: true,
        options: [
          { value: "household", label: "Household" },
          { value: "task", label: "Task" },
          { value: "opportunity", label: "Opportunity" },
          { value: "meeting", label: "Meeting" },
          { value: "person", label: "Person" },
        ],
      },
      {
        key: "prompt",
        label: "Email prompt",
        inputType: "textarea",
        required: true,
        helperText: "Data from the associated record will automatically be prepended to the prompt.",
        supportsInterpolation: true,
      },
    ],
  },
  {
    key: "action__ai_research",
    name: "Research",
    description: "Use AI to research and gather information",
    category: "ai",
    nodeType: "action",
    icon: Search,
    resultSchema: RESEARCH_RESULT_SCHEMA,
    documentation: RESEARCH_DOCUMENTATION,
    fields: [
      {
        key: "step_name",
        label: "Step name",
        inputType: "string",
        required: false,
        placeholder: "e.g. Meeting prep research",
        helperText: "Used to identify this research step and its results in variable pickers.",
      },
      {
        key: "research_subject",
        label: "Research subject",
        inputType: "select",
        required: true,
        subjectOptions: true,
        options: [
          { value: "household", label: "Household" },
          { value: "task", label: "Task" },
          { value: "opportunity", label: "Opportunity" },
          { value: "meeting", label: "Meeting" },
        ],
      },
      {
        key: "research_query",
        label: "Research prompt",
        inputType: "textarea",
        required: true,
        helperText: "Subject information will automatically be added to the prompt.",
        supportsInterpolation: true,
      },
    ],
  },
  {
    key: "action__ai_parse_and_create_contact",
    name: "Parse and create contact",
    description: "Extract contact information from email or meeting and create a record",
    category: "ai",
    nodeType: "action",
    icon: UserPlus,
    requiresAnySubject: ["email_message", "meeting"],
    providesSubjects: ["person"],
    providesSubjectsByFields: [
      {
        field: "new_record_type",
        values: {
          client: ["household"],
          prospect: ["household"],
          contact: ["contact"],
        },
      },
    ],
    singlePerBranch: true,
    documentation: PARSE_CONTACT_DOCUMENTATION,
    fields: [
      {
        key: "new_record_type",
        label: "Type of record to create",
        inputType: "select",
        required: true,
        defaultValue: "prospect",
        helperText: "Stops if this record type already exists in the book with the same email.",
        options: [
          { value: "client", label: "Client" },
          { value: "prospect", label: "Prospect" },
          { value: "contact", label: "Contact" },
        ],
      },
      {
        key: "specific_instructions",
        label: "Additional instructions",
        inputType: "markdown",
        required: false,
        helperText: "Optional instructions for the AI when parsing contact information.",
        supportsInterpolation: true,
      },
    ],
  },
]
